<template>
  <q-select
    v-bind="$attrs"
    :model-value="modelValue"
    :options="displayOptions"
    :option-label="optionLabel"
    :option-value="optionValue"
    :use-input="searchable"
    :input-debounce="200"
    :virtual-scroll-slice-size="useVirtualScroll ? 30 : undefined"
    :option-disable="opt => opt.__separator === true"
    @filter="onFilter"
    @update:model-value="onUpdateModelValue"
    @popup-show="onPopupShow"
    @popup-hide="onPopupHide"
  >
    <template #no-option>
      <q-item>
        <q-item-section class="text-grey">
          Aucun résultat
        </q-item-section>
      </q-item>
    </template>

    <template #option="scope">
      <!-- Séparateur de groupe -->
      <template v-if="scope.opt.__separator">
        <q-item-label header class="text-weight-bold q-py-sm" style="background-color: #f5f5f5;">
          <q-icon :name="scope.opt.__groupIcon" class="q-mr-sm" />
          {{ scope.opt.__groupLabel }} ({{ scope.opt.__groupCount }})
        </q-item-label>
      </template>
      <!-- Option normale -->
      <q-item v-else v-bind="scope.itemProps">
        <q-item-section v-if="showIcons" avatar style="min-width: 32px;">
          <q-icon :name="getFileIcon(scope.opt)" size="sm" />
        </q-item-section>
        <q-item-section>
          <q-item-label>{{ getLabel(scope.opt) }}</q-item-label>
        </q-item-section>
      </q-item>
    </template>

    <!-- Passer les slots du parent (sauf ceux qu'on gère) -->
    <template v-for="(_, name) in $slots" :key="name" #[name]="scope">
      <slot v-if="name !== 'option' && name !== 'no-option'" :name="name" v-bind="scope || {}" />
    </template>
  </q-select>
</template>

<script>
import { ref, computed, watch } from 'vue';
export default {
  name: 'FileSelect',
  inheritAttrs: false,
  props: {
    modelValue: {
      default: undefined
    },
    options: {
      type: Array,
      required: true
    },
    optionLabel: {
      type: [String, Function],
      default: 'title'
    },
    optionValue: {
      type: [String, Function],
      default: undefined
    },
    grouped: {
      type: Boolean,
      default: true
    },
    showIcons: {
      type: Boolean,
      default: true
    },
    searchable: {
      type: Boolean,
      default: true
    }
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const searchText = ref('');
    const filteredOptions = ref([]);

    const EXT_MAP = {
      Images: {
        extensions: ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'tiff', 'bmp'],
        icon: 'image'
      },
      Videos: {
        extensions: ['mp4', 'avi', 'mov', 'wmv', 'mkv', 'webm'],
        icon: 'videocam'
      },
      Audios: {
        extensions: ['mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a'],
        icon: 'audiotrack'
      },
      Documents: {
        extensions: ['doc', 'docx', 'pdf', 'txt', 'rtf', 'odt'],
        icon: 'description'
      }
    };

    const CATEGORY_ORDER = ['Images', 'Videos', 'Audios', 'Documents', 'Autres'];

    const getExtension = (opt) => {
      const name = opt && opt.name ? opt.name : '';
      const parts = name.split('.');
      return parts.length > 1 ? parts.pop().toLowerCase() : '';
    };

    const getCategory = (opt) => {
      const ext = getExtension(opt);
      for (const [category, config] of Object.entries(EXT_MAP)) {
        if (config.extensions.includes(ext)) {
          return category;
        }
      }
      return 'Autres';
    };

    const getCategoryIcon = (category) => {
      if (EXT_MAP[category]) {
        return EXT_MAP[category].icon;
      }
      return 'insert_drive_file';
    };

    const getFileIcon = (opt) => {
      const category = getCategory(opt);
      return getCategoryIcon(category);
    };

    const getLabel = (opt) => {
      if (typeof props.optionLabel === 'function') {
        return props.optionLabel(opt);
      }
      return opt && opt[props.optionLabel] !== undefined ? opt[props.optionLabel] : '';
    };

    const buildGroupedOptions = (opts) => {
      if (!props.grouped || !opts || opts.length === 0) {
        return opts || [];
      }

      // Group by category
      const groups = {};
      for (const opt of opts) {
        const cat = getCategory(opt);
        if (!groups[cat]) groups[cat] = [];
        groups[cat].push(opt);
      }

      // Sort within groups alphabetically
      for (const cat of Object.keys(groups)) {
        groups[cat].sort((a, b) => {
          const labelA = (getLabel(a) ?? '').toString().toUpperCase();
          const labelB = (getLabel(b) ?? '').toString().toUpperCase();
          if (labelA < labelB) return -1;
          if (labelA > labelB) return 1;
          return 0;
        });
      }

      // Build final array in category order
      const result = [];
      for (const cat of CATEGORY_ORDER) {
        if (groups[cat] && groups[cat].length > 0) {
          result.push({
            __separator: true,
            __groupLabel: cat,
            __groupIcon: getCategoryIcon(cat),
            __groupCount: groups[cat].length
          });
          result.push(...groups[cat]);
        }
      }
      return result;
    };

    const filterOptions = (val) => {
      const opts = props.options || [];
      if (!val || val.trim() === '') {
        filteredOptions.value = buildGroupedOptions(opts);
        return;
      }
      const needle = val.toLowerCase();
      const filtered = opts.filter((opt) => {
        const label = (getLabel(opt) ?? '').toString().toLowerCase();
        return label.includes(needle);
      });
      filteredOptions.value = buildGroupedOptions(filtered);
    };

    const displayOptions = computed(() => {
      return filteredOptions.value;
    });

    const useVirtualScroll = computed(() => {
      return props.options && props.options.length >= 50;
    });

    // Initialize filteredOptions
    filteredOptions.value = buildGroupedOptions(props.options || []);

    // Watch for changes in props.options (async data)
    watch(() => props.options, (newOpts) => {
      filteredOptions.value = buildGroupedOptions(newOpts || []);
    }, { deep: true });

    const onFilter = (val, update) => {
      update(() => {
        filterOptions(val);
      });
    };

    const onUpdateModelValue = (val) => {
      // Guard: filter out separators
      if (Array.isArray(val)) {
        val = val.filter(v => !v || v.__separator !== true);
      } else if (val && val.__separator === true) {
        return;
      }
      emit('update:modelValue', val);
    };

    const onPopupShow = () => {
      filteredOptions.value = buildGroupedOptions(props.options || []);
    };

    const onPopupHide = () => {
      searchText.value = '';
      filteredOptions.value = buildGroupedOptions(props.options || []);
    };

    return {
      displayOptions,
      useVirtualScroll,
      searchText,
      getFileIcon,
      getLabel,
      onFilter,
      onUpdateModelValue,
      onPopupShow,
      onPopupHide
    };
  }
};
</script>
